import { CardContent, CardShellEcho } from '@/components/cards/common';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';
import MapChartEcho from '@/routes/AudiencePage/charts/MapChart/MapChartEcho';
import { LocationPlaysResponse, SelectedPlatform } from '@/types/api/location';
import useFetchQuery from '@/hooks/useFetchQuery';
import { useEffect, useMemo, useState } from 'react';
import { useLocationPlaysQuery } from '@/services/revenueApi';
import { mixpanelTrack, RevenueDashboardWidget } from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import { getFirstDisplayPlatform } from '@/utils/platformUtils';
import LocationModalEcho from './LocationModalEcho';
import DataAvailabilityBanner from './DataAvailabilityBanner';
import useIsBeforeDataAvailability from './useIsBeforeDataAvailability';
import { Flex, theme } from 'antd';

const LocationCardEcho = ({ className }: { className?: string }) => {
  const isBeforeDataAvailability = useIsBeforeDataAvailability('location');
  const { queryResult } = useFetchQuery(useLocationPlaysQuery, undefined, { sources: [] });

  const { data: locationPlays } = queryResult;

  const platforms = useMemo(() => {
    return [...new Set(locationPlays?.plays?.map((play) => play.platform) ?? [])];
  }, [locationPlays]);

  type SelectedPlatform = (typeof platforms)[number];

  const [selectedPlatform, setSelectedPlatform] = useState<SelectedPlatform | null>(
    platforms?.[0] ?? null
  );

  const handlePlatformChange = (platform: SelectedPlatform) => {
    setSelectedPlatform(platform);

    mixpanelTrack(MIXPANEL_EVENT.audience.demographics.platformRadioSelected, {
      page: 'audience',
      widget: RevenueDashboardWidget.LocationDemographics,
      platform: platform,
      demographics: 'location',
    });
  };

  useEffect(() => {
    setSelectedPlatform(
      getFirstDisplayPlatform({ plays: platforms.map((p) => ({ platform: p })) })
    );
  }, [platforms]);

  return (
    <CardShellEcho
      className={className}
      title="Top locations"
      headerOptions={locationPlays?.plays?.length && <LocationModalEcho />}
    >
      {isBeforeDataAvailability ? (
        <DataAvailabilityBanner dataType="location" />
      ) : (
        <CardContent
          variant="echo"
          {...queryResult}
          renderData={(locationData: LocationPlaysResponse) => (
            <Data
              data={locationData}
              selectedPlatform={selectedPlatform}
              handlePlatformChange={handlePlatformChange}
            />
          )}
        />
      )}
    </CardShellEcho>
  );
};

const Data = ({
  data,
  selectedPlatform,
  handlePlatformChange,
}: {
  data: LocationPlaysResponse;
  selectedPlatform: SelectedPlatform<typeof data> | null;
  handlePlatformChange: (platform: SelectedPlatform<typeof data>) => void;
}) => {
  const { token } = theme.useToken();
  if (data?.plays?.length < 1 || selectedPlatform === null) {
    return <NoDataMessageEcho dataType="location data" />;
  }

  return (
    <Flex vertical justify="space-between" gap={token.margin} style={{ marginTop: token.marginLG }}>
      <MapChartEcho
        data={data}
        selectedPlatform={selectedPlatform}
        handlePlatformChange={handlePlatformChange}
      />
    </Flex>
  );
};

export default LocationCardEcho;
